import React, { useState } from 'react';
import { Container, Box, Button, Grid, Typography, Tabs, Tab, Paper } from '@mui/material';
import ThermalInvoiceFormatRegular from '../components/templates/ThermalInvoiceFormatRegular';
import ThermalInvoiceFormatUnregistered from '../components/templates/ThermalInvoiceFormatUnregistered';
import ThermalInvoiceFormatComposition from '../components/templates/ThermalInvoiceFormatComposition';
import { useReactToPrint } from 'react-to-print';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`thermal-tabpanel-${index}`}
      aria-labelledby={`thermal-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const InvoiceThermalDemo = () => {
  const [isPrinting, setIsPrinting] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const regularRef = React.useRef(null);
  const unregisteredRef = React.useRef(null);
  const compositionRef = React.useRef(null);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  // Sample business details (common base)
  const businessDetailsBase = {
    name: 'AkMo Technologies',
    address: '123 Tech Park, Silicon Valley, CA 94024',
    phone: '+1 (555) 123-4567',
    email: 'contact@akmotech.com',
  };

  // Regular GST registered business
  const regularBusinessDetails = {
    ...businessDetailsBase,
    gstin: '29AABCU9603R1ZP',
    gstRegistered: 'registered' as 'registered',
    gstType: 'regular' as 'regular',
  };

  // Composition scheme business
  const compositionBusinessDetails = {
    ...businessDetailsBase,
    gstin: '29AABCU9603R1ZP',
    gstRegistered: 'registered' as 'registered',
    gstType: 'composition' as 'composition',
  };

  // Unregistered business
  const unregisteredBusinessDetails = {
    ...businessDetailsBase,
    gstRegistered: 'not_registered' as 'not_registered',
  };

  // Sample client details
  const clientDetails = {
    name: 'Innovative Solutions Inc.',
    address: '456 Business Avenue, Suite 200, New York, NY 10001',
    phone: '+1 (555) 987-6543',
    email: 'accounts@innovativesolutions.com',
    gstin: '07AAACI7573H1ZV'
  };

  // Sample invoice details (common base)
  const invoiceDetailsBase = {
    invoiceNumber: 'INV-2023-0042',
    invoiceDate: '2023-11-15',
    isInterstate: false,
    items: [
      {
        description: 'Web Development Services',
        quantity: 1,
        rate: 50000,
        gstRate: 18,
        amount: 59000
      },
      {
        description: 'Mobile App Development',
        quantity: 1,
        rate: 35000,
        gstRate: 18,
        amount: 41300
      },
      {
        description: 'SEO Package - 3 months',
        quantity: 1,
        rate: 12000,
        gstRate: 18,
        amount: 14160
      }
    ],
    discount: 5000,
    additionalCharges: [
      {
        name: 'Express Delivery',
        amount: 1500
      }
    ],
    termsAndConditions: [
      'Payment due within 15 days',
      'All services are non-refundable',
      'Late payments subject to 5% charge'
    ]
  };

  // Handle print functionality for each format
  const handlePrintRegular = useReactToPrint({
    content: () => regularRef.current,
    onBeforeGetContent: () => {
      setIsPrinting(true);
      return Promise.resolve();
    },
    onAfterPrint: () => {
      setIsPrinting(false);
    }
  });

  const handlePrintUnregistered = useReactToPrint({
    content: () => unregisteredRef.current,
    onBeforeGetContent: () => {
      setIsPrinting(true);
      return Promise.resolve();
    },
    onAfterPrint: () => {
      setIsPrinting(false);
    }
  });

  const handlePrintComposition = useReactToPrint({
    content: () => compositionRef.current,
    onBeforeGetContent: () => {
      setIsPrinting(true);
      return Promise.resolve();
    },
    onAfterPrint: () => {
      setIsPrinting(false);
    }
  });

  return (
    <Container maxWidth="lg">
      <Box sx={{ my: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom align="center">
          Thermal Invoice Formats Demo
        </Typography>
        
        <Paper sx={{ borderRadius: 2, overflow: 'hidden', mb: 3 }}>
          <Tabs 
            value={tabValue} 
            onChange={handleTabChange} 
            aria-label="thermal invoice formats"
            centered
            sx={{ borderBottom: 1, borderColor: 'divider' }}
          >
            <Tab label="Regular GST" />
            <Tab label="Composition Scheme" />
            <Tab label="Unregistered" />
          </Tabs>

          <TabPanel value={tabValue} index={0}>
            <Grid container spacing={2} justifyContent="center">
              <Grid item xs={12} textAlign="center">
                <Typography variant="h6" gutterBottom>
                  Regular GST Format
                </Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  For businesses registered under regular GST scheme. Shows CGST/SGST or IGST.
                </Typography>
                <Button 
                  variant="contained" 
                  onClick={handlePrintRegular}
                  sx={{ mb: 2 }}
                >
                  Print Regular Format
                </Button>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box ref={regularRef} sx={{ mx: 'auto', width: 'fit-content' }}>
                  <ThermalInvoiceFormatRegular
                    businessDetails={regularBusinessDetails}
                    clientDetails={clientDetails}
                    invoiceDetails={invoiceDetailsBase}
                  />
                </Box>
              </Grid>
            </Grid>
          </TabPanel>
          
          <TabPanel value={tabValue} index={1}>
            <Grid container spacing={2} justifyContent="center">
              <Grid item xs={12} textAlign="center">
                <Typography variant="h6" gutterBottom>
                  Composition Scheme Format
                </Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  For businesses under composition scheme. Displays special notice at the bottom.
                </Typography>
                <Button 
                  variant="contained" 
                  onClick={handlePrintComposition}
                  sx={{ mb: 2 }}
                >
                  Print Composition Format
                </Button>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box ref={compositionRef} sx={{ mx: 'auto', width: 'fit-content' }}>
                  <ThermalInvoiceFormatComposition
                    businessDetails={compositionBusinessDetails}
                    clientDetails={clientDetails}
                    invoiceDetails={invoiceDetailsBase}
                  />
                </Box>
              </Grid>
            </Grid>
          </TabPanel>
          
          <TabPanel value={tabValue} index={2}>
            <Grid container spacing={2} justifyContent="center">
              <Grid item xs={12} textAlign="center">
                <Typography variant="h6" gutterBottom>
                  Unregistered Format
                </Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  For businesses not registered under GST. Simplified format without tax details.
                </Typography>
                <Button 
                  variant="contained" 
                  onClick={handlePrintUnregistered}
                  sx={{ mb: 2 }}
                >
                  Print Unregistered Format
                </Button>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box ref={unregisteredRef} sx={{ mx: 'auto', width: 'fit-content' }}>
                  <ThermalInvoiceFormatUnregistered
                    businessDetails={unregisteredBusinessDetails}
                    clientDetails={clientDetails}
                    invoiceDetails={invoiceDetailsBase}
                  />
                </Box>
              </Grid>
            </Grid>
          </TabPanel>
        </Paper>
      </Box>
    </Container>
  );
};

export default InvoiceThermalDemo; 